import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Group,
  Paper,
  Stack,
  Text,
  TextInput,
  UnstyledButton,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { IconArrowLeft, IconExternalLink } from '@tabler/icons-react';
import useApi from '../hooks/useApi';
import { createHostedPage, fetchMyPrincipal, updateArticle } from '../services/api';

function slugify(text) {
  return String(text || '')
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 60);
}

function isHttpUrl(value) {
  try {
    const parsed = new URL(String(value || '').trim());
    return parsed.protocol === 'http:' || parsed.protocol === 'https:';
  } catch {
    return false;
  }
}

export default function AviaryV2ArticleNewExternalPage() {
  const api = useApi();
  const navigate = useNavigate();
  const location = useLocation();
  const back = location.state?.back || { path: '/v2/articles/new', label: 'Add' };
  const defaultRole = location.state?.defaultRole || 'promo';

  const [title, setTitle] = useState('');
  const [slug, setSlug] = useState('');
  const [slugDirty, setSlugDirty] = useState(false);
  const [externalUrl, setExternalUrl] = useState('');
  const [principalId, setPrincipalId] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let cancelled = false;
    fetchMyPrincipal(api)
      .then((principal) => {
        if (cancelled) return;
        setPrincipalId(String(principal?.id || '').trim());
      })
      .catch(() => {
        if (cancelled) return;
        setPrincipalId('');
      });
    return () => { cancelled = true; };
  }, [api]);

  function onTitleBlur() {
    if (!slugDirty) setSlug(slugify(title));
  }

  function onSlugChange(value) {
    setSlug(slugify(value));
    setSlugDirty(true);
  }

  async function onSubmit() {
    const trimmedTitle = String(title || '').trim();
    const trimmedSlug = String(slug || '').trim();
    const trimmedUrl = String(externalUrl || '').trim();
    if (!trimmedTitle || !trimmedSlug || !trimmedUrl) return;
    if (!isHttpUrl(trimmedUrl)) {
      notifications.show({
        title: 'Invalid URL',
        message: 'External URL must start with http:// or https://',
        color: 'red',
      });
      return;
    }

    setSaving(true);
    try {
      const page = await createHostedPage(api, {
        kind: 'external',
        title: trimmedTitle,
        slug: trimmedSlug,
        linkedUrl: trimmedUrl,
        articleStatus: 'published',
        principalId: principalId || undefined,
      });
      if (defaultRole === 'context') {
        await updateArticle(api, page.slug, { articleRole: 'context' });
      }
      const nextPath = defaultRole === 'context'
        ? '/v2/knowledge-base'
        : `/v2/articles/${encodeURIComponent(page.slug)}`;
      navigate(nextPath, {
        state: { back: { path: '/v2/articles/new/external', label: 'External URL' }, defaultRole },
      });
    } catch (err) {
      notifications.show({
        title: 'Could not create external article',
        message: err.response?.data?.error || err.message,
        color: 'red',
      });
    } finally {
      setSaving(false);
    }
  }

  const canSubmit = title.trim() && slug.trim() && externalUrl.trim();

  return (
    <Box style={{ padding: '32px 24px 120px', maxWidth: 760, margin: '0 auto' }}>
      <Stack gap="lg">
        <UnstyledButton
          onClick={() => navigate(back.path)}
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 6,
            color: 'var(--aviary-muted)',
            fontSize: 13,
            width: 'fit-content',
          }}
        >
          <IconArrowLeft size={14} /> Back to {back.label}
        </UnstyledButton>

        <Stack gap={4}>
          <Text fw={700} style={{ color: 'var(--aviary-ink)', fontSize: 28 }}>
            External URL
          </Text>
          <Text size="sm" style={{ color: 'var(--aviary-muted)' }}>
            Create an external article record that points to your hosted page.
          </Text>
        </Stack>

        <Paper p="lg" radius="md" withBorder>
          <Stack gap="md">
            <TextInput
              label="Title"
              value={title}
              onChange={(e) => setTitle(e.currentTarget.value)}
              onBlur={onTitleBlur}
              required
            />
            <TextInput
              label="Slug"
              description="lowercase kebab-case"
              value={slug}
              onChange={(e) => onSlugChange(e.currentTarget.value)}
              required
            />
            <TextInput
              label="External URL"
              placeholder="https://example.com/test-article"
              value={externalUrl}
              onChange={(e) => setExternalUrl(e.currentTarget.value)}
              leftSection={<IconExternalLink size={15} />}
              required
            />
            <Group justify="flex-end">
              <Button color="sage" onClick={onSubmit} disabled={!canSubmit || saving} loading={saving}>
                Create article
              </Button>
            </Group>
          </Stack>
        </Paper>
      </Stack>
    </Box>
  );
}
